import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { EmailCampaignData, Fan, ScheduleOptions } from 'modules/Types'
import { getEmailUsage_api } from 'api/brandApi'
import useBrand from './useBrand'
import { BrandExistsState } from './useBrand'
import { EmailTemplateData, sendEmailTemplateData_api } from 'api/campaignApi'
import { ITimezoneOption } from 'components/shareable/TimezoneSelect'
import dayjs, { Dayjs } from 'dayjs'
import useUser from './useUser'
import { UserExistsState } from './useUser'

const { ASAP } = ScheduleOptions

/** Design state for email message composition */
interface Design {
    initialEmailTemplate?: string
    emailTemplate?: string
    subjectLine: string
}

/** Details state for message scheduling and recipients */
interface Details {
    fans: Fan[]
    date: Dayjs | null
    scheduled: ScheduleOptions
    timezone: ITimezoneOption
}

/** State interface for the message your fans store */
interface MessageYourFansState {
    // State
    design: Design
    details: Details
    showPostCreation: boolean
    isLoadingMessageYourFans: boolean
    isPoweredBySymphony: boolean

    // Actions
    /** Updates the email template content */
    setEmailTemplate: (emailTemplate: string) => void
    /** Updates the email subject line */
    setSubjectLine: (subjectLine: string) => void
    /** Updates the design state with new values */
    setDesign: (design: Partial<Design>) => void
    /** Updates the details state with new values */
    setDetails: (details: Details) => void
    /** Sets the post creation modal visibility */
    setShowPostCreation: (show: boolean) => void
    /** Sets the loading state */
    setIsLoadingMessageYourFans: (isLoadingMessageYourFans: boolean) => void
    /** Gets the email usage for the current brand */
    getEmailUsage: () => Promise<{ usage: number }>
    /** Sends the email template data to the API */
    sendEmailTemplateData: (emailTemplateData: EmailTemplateData) => Promise<EmailCampaignData>
    /** Sets the powered by Symphony state */
    setIsPoweredBySymphony: (isPoweredBySymphony: boolean) => void

    // Methods
    getFinalHtmlContent: () => string
}

const useMessageYourFans = create(immer<MessageYourFansState>((set, get) => ({
    design: {
        subjectLine: '',
        emailTemplate: '',
    },
    details: {
        fans: [],
        date: dayjs().tz('America/Los_Angeles'),
        scheduled: ASAP,
        timezone: { value: 'America/Los_Angeles', abbrev: 'PDT', title: 'America / Los Angeles [PDT]' },
    },
    showPostCreation: false,
    isLoadingMessageYourFans: false,
    isPoweredBySymphony: true,
    setEmailTemplate: (emailTemplate: string) => set((state) => { state.design.emailTemplate = emailTemplate }),

    setSubjectLine: (subjectLine: string) => set((state) => { state.design.subjectLine = subjectLine }),

    setDesign: (newDesign) => set((state) => {
        state.design = { ...state.design, ...newDesign }
    }),

    setDetails: (newDetails) => set((state) => {
        state.details = newDetails
    }),

    setShowPostCreation: (show) => set((state) => { state.showPostCreation = show }),

    setIsLoadingMessageYourFans: (isLoadingMessageYourFans) => set((state) => { state.isLoadingMessageYourFans = isLoadingMessageYourFans }),

    getEmailUsage: async () => {
        try {
            set((state) => { state.isLoadingMessageYourFans = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;
            const emailUsage = await getEmailUsage_api(currentBrand.id);
            return emailUsage;
        } catch (error) {
            console.error('Error getting email usage:', error);
            throw error;
        } finally {
            set((state) => { state.isLoadingMessageYourFans = false });
        }
    },

    sendEmailTemplateData: async (emailTemplateData: EmailTemplateData) => {
        try {
            set((state) => { state.isLoadingMessageYourFans = true });
            const { currentBrand } = useBrand.getState() as BrandExistsState;
            const response = await sendEmailTemplateData_api(currentBrand.id, emailTemplateData);
            return response;
        } catch (error) {
            console.error('Error sending email template data:', error);
            throw error;
        } finally {
            set((state) => { state.isLoadingMessageYourFans = false });
        }
    },

    setIsPoweredBySymphony: (isPoweredBySymphony: boolean) => set((state) => { state.isPoweredBySymphony = isPoweredBySymphony }),

    getFinalHtmlContent: () => {
        const { currentUser } = useUser.getState() as UserExistsState;
        const { isPoweredBySymphony, design } = get();
        const { emailTemplate } = design;

        let emailTemplateLocal = emailTemplate || '';

        const referralLink = currentUser.metadata?.rewardful_affiliate_link;

        if (isPoweredBySymphony) {
            emailTemplateLocal = emailTemplateLocal + `
            <p>&nbsp;</p>
            <p>&nbsp;</p>
            <p style="text-align: center; background-color: #f5f5f5; width: fit-content; margin: 0 auto; padding: 8px 16px; border-radius: 4px;"><a href={currentUser.metadata?.rewardful_affiliate_link} style="display: inline-flex; align-items: center;">Powered by <img src="https://symphony.to/logos/symphony-badge-purple.png" style="height: 24px; vertical-align: middle; margin-left: 4px;" /></a></p>
            `;
        }

        return emailTemplateLocal
    }
})))

export default useMessageYourFans;